import React, { useEffect, useState } from "react";
import { formatDate, tradeGet, tradeJson } from "../../utils/tradeApi";

export default function TradeNotificationPanel({ projectId, category, files, onSent }) {
  const [preview, setPreview] = useState(null);
  const [toInput, setToInput] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [subject, setSubject] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const pendingCount = files.filter((file) => file.status === "pending" || file.notification_status === "pending").length;
  const parseAddresses = (value) => [...new Set(value.split(/[,;\n]/).map((item) => item.trim().toLowerCase()).filter(Boolean))];
  const currentTo = parseAddresses(toInput);
  const currentCc = parseAddresses(ccInput).filter((address) => !currentTo.includes(address));
  const canSend = currentTo.length > 0 && Boolean(subject.trim());

  async function loadHistory() {
    if (!category) return;
    try {
      setHistory(await tradeGet(`/api/trade/notifications/history?projectId=${projectId}&categoryId=${category._id}`));
    } catch (requestError) { setError(requestError.message); }
  }

  useEffect(() => { setPreview(null); setToInput(""); setCcInput(""); setSubject(""); setError(""); loadHistory(); }, [projectId, category?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function createPreview() {
    setLoading(true); setError("");
    try {
      const result = await tradeJson("/api/trade/notifications/preview", "POST", { projectId, categoryId: category._id });
      setPreview(result);
      setToInput((result.to || []).join(", "));
      setCcInput((result.cc || []).join(", "));
      setSubject(result.subject || "");
    }
    catch (requestError) { setError(requestError.message); }
    finally { setLoading(false); }
  }

  async function send() {
    if (!preview || !canSend) return;
    setLoading(true); setError("");
    try {
      await tradeJson("/api/trade/notifications/send", "POST", {
        projectId,
        categoryId: category._id,
        to: currentTo,
        cc: currentCc,
        subject: subject.trim(),
      });
      setPreview(null); await loadHistory(); await onSent();
    } catch (requestError) { setError(requestError.message); }
    finally { setLoading(false); }
  }

  async function retry(batchId) {
    setLoading(true); setError("");
    try { await tradeJson(`/api/trade/notifications/${batchId}/retry`, "POST"); await loadHistory(); await onSent(); }
    catch (requestError) { setError(requestError.message); }
    finally { setLoading(false); }
  }

  if (!category) return <div className="trade-empty">Select a category first.</div>;

  return (
    <div className="trade-notification-grid">
      <section className="trade-card">
        <span className="trade-eyebrow trade-green">NOTIFICATION</span>
        <h2>{category.display_name || `${category.code} ${category.name}`}</h2>
        <p className="trade-muted">Review the recipients below before sending. Addresses removed from To or CC will not receive this email.</p>
        <div className="trade-notify-summary"><strong>{pendingCount}</strong><span>pending file{pendingCount === 1 ? "" : "s"} ready to notify</span></div>
        {error && <div className="trade-alert error">{error}</div>}
        {!preview && <button className="trade-button primary" onClick={createPreview} disabled={!pendingCount || loading}>{loading ? "Preparing…" : "Preview email"}</button>}
        {preview && (
          <div className="trade-email-preview">
            <div className="trade-email-fields">
              <div className="trade-email-readonly"><span>From</span><strong>{preview.from}</strong></div>
              <label>To<textarea value={toInput} onChange={(event) => setToInput(event.target.value)} rows="2" placeholder="recipient@example.com" /></label>
              <label>CC<textarea value={ccInput} onChange={(event) => setCcInput(event.target.value)} rows="2" placeholder="project.manager@example.com" /></label>
              <label>Subject<input value={subject} onChange={(event) => setSubject(event.target.value)} maxLength="200" /></label>
              <small>Separate multiple email addresses with commas, semicolons, or new lines. Default recipients may be removed. Addresses listed in To are removed from CC automatically.</small>
            </div>
            {!currentTo.length && <div className="trade-alert warning">At least one To recipient is required.</div>}
            {!currentCc.length && <div className="trade-alert warning">No CC recipients will receive this notification.</div>}
            <pre>{preview.text}</pre>
            <div className="trade-action-row"><button className="trade-button secondary" onClick={() => setPreview(null)}>Close preview</button><button className="trade-button secondary" onClick={() => { setToInput((preview.to || []).join(", ")); setCcInput((preview.cc || []).join(", ")); setSubject(preview.subject || ""); }}>Restore defaults</button><button className="trade-button primary" onClick={send} disabled={!canSend || loading}>{loading ? "Sending…" : `Send to ${currentTo.length} recipient${currentTo.length === 1 ? "" : "s"}`}</button></div>
          </div>
        )}
      </section>
      <section className="trade-card">
        <span className="trade-eyebrow trade-green">AUDIT LOG</span><h2>Notification history</h2>
        {!history.length && <div className="trade-empty small">No notifications sent for this category.</div>}
        <div className="trade-history-list">
          {history.map((batch) => (
            <article key={batch._id}>
              <div><span className={`trade-status ${batch.status}`}>{batch.status}</span><strong>{batch.snapshot?.files?.length || batch.file_ids?.length || 0} file(s)</strong></div>
              <small>{formatDate(batch.created_at, true)} · To: {(batch.to || []).join(", ") || "—"}</small>
              <small>CC: {(batch.cc || []).join(", ") || "—"}</small>
              <small>Subject: {batch.snapshot?.subject || "—"}</small>
              {batch.resend_message_id && <small>Resend ID: {batch.resend_message_id}</small>}
              {batch.last_error && <small className="trade-error-text">{batch.last_error}</small>}
              {batch.status === "failed" && <button className="trade-link-button" onClick={() => retry(batch._id)} disabled={loading}>Retry</button>}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
